import { BookOpen, Bot, Clock3, GitBranch, ListTree, Network } from "lucide-react";
import { ArtifactKindEnum } from "@tubo/api-models/business/kb/knowledge-compilation.enums";

/** 编译中心展示的六条业务路径（topicIndex 为内部向量索引，不进产物导航） */
export type CompilationKind = Exclude<ArtifactKindEnum, "topicIndex">;

export interface CompilationKindMeta {
  kind: CompilationKind;
  labelKey: string;
  descriptionKey: string;
  icon: typeof ListTree;
  /** 详情面板/图标 chip 的路径着色（chart 色板，六类一眼可区分）。 */
  colorClass: string;
}

export const COMPILATION_KIND_META: CompilationKindMeta[] = [
  { kind: ArtifactKindEnum.TreeProjection, labelKey: "compilation.kinds.treeProjection", descriptionKey: "compilation.kinds.treeProjectionDescription", icon: ListTree, colorClass: "bg-chart-1/10 text-chart-1" },
  { kind: ArtifactKindEnum.Timeline, labelKey: "compilation.kinds.timeline", descriptionKey: "compilation.kinds.timelineDescription", icon: Clock3, colorClass: "bg-chart-4/10 text-chart-4" },
  { kind: ArtifactKindEnum.DiscoveryGraph, labelKey: "compilation.kinds.discoveryGraph", descriptionKey: "compilation.kinds.discoveryGraphDescription", icon: Network, colorClass: "bg-chart-3/10 text-chart-3" },
  { kind: ArtifactKindEnum.MindMap, labelKey: "compilation.kinds.mindMap", descriptionKey: "compilation.kinds.mindMapDescription", icon: GitBranch, colorClass: "bg-chart-5/10 text-chart-5" },
  { kind: ArtifactKindEnum.Wiki, labelKey: "compilation.kinds.wiki", descriptionKey: "compilation.kinds.wikiDescription", icon: BookOpen, colorClass: "bg-chart-2/10 text-chart-2" },
  { kind: ArtifactKindEnum.Skills, labelKey: "compilation.kinds.skills", descriptionKey: "compilation.kinds.skillsDescription", icon: Bot, colorClass: "bg-primary/10 text-primary" },
];

export function compilationKindMeta(kind: string): CompilationKindMeta | undefined {
  return COMPILATION_KIND_META.find((meta) => meta.kind === kind);
}

const KIND_LABEL_KEY_MAP = new Map<string, string>(
  COMPILATION_KIND_META.map((meta): [string, string] => [meta.kind, meta.labelKey]),
);

export function compilationKindLabel(kind: string, translate: (key: string) => string): string {
  const key = KIND_LABEL_KEY_MAP.get(kind);
  return key ? translate(key) : kind;
}

export function isCompilationKind(value: unknown): value is CompilationKind {
  return typeof value === "string" && KIND_LABEL_KEY_MAP.has(value);
}

const RUN_STATUS_KEYS: Record<string, string> = {
  building: "compilation.runStatuses.building",
  fullDone: "compilation.runStatuses.fullDone",
  warnDone: "compilation.runStatuses.warnDone",
  failed: "compilation.runStatuses.failed",
  cancelled: "compilation.runStatuses.cancelled",
};

export function compilationRunStatusLabel(status: string, translate: (key: string) => string): string {
  const key = RUN_STATUS_KEYS[status];
  return key ? translate(key) : status;
}

const ARTIFACT_STATUS_KEYS: Record<string, string> = {
  active: "compilation.artifactStatuses.active",
  building: "compilation.artifactStatuses.building",
  stale: "compilation.artifactStatuses.stale",
  failed: "compilation.artifactStatuses.failed",
};

export function compilationArtifactStatusLabel(status: string, translate: (key: string) => string): string {
  const key = ARTIFACT_STATUS_KEYS[status];
  return key ? translate(key) : status;
}

const SCOPE_KEYS: Record<string, string> = {
  document: "compilation.scopes.document",
  knowledgeBase: "compilation.scopes.knowledgeBase",
};

export function compilationScopeLabel(scope: string, translate: (key: string) => string): string {
  const key = SCOPE_KEYS[scope];
  return key ? translate(key) : scope;
}

export function compilationRunStatusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "building") return "default";
  if (status === "failed") return "destructive";
  if (status === "fullDone") return "secondary";
  return "outline";
}

export function compilationArtifactStatusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "building") return "default";
  if (status === "failed") return "destructive";
  if (status === "active") return "secondary";
  return "outline";
}
